import { IStyle } from 'Styles';
import { ImageManagementTaskDTO } from 'business/models/Models';
import ImageManagementTaskService from 'business/services/ImageManagementTaskService';
import { ButtonsListContainer } from 'components/Button';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { SelectInput } from 'components/input';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useImageManagementTaskIsDirty,
  useImageManagementTasks,
  useIsImageManagementTaskListLoading,
  useSelectedImageTaskId,
} from 'redux/hooks/imageManagementTask';
import { showAlertSnackBar } from 'utils/Global';
import {
  deserializeImageTaskDTO,
  getStorageInfoModalProps,
  showLargerTaskNameMessage,
  showUnsavedMessage,
  taskButtons,
  TASK_NAME_MAXIMUM_LENGTH,
  getUniqueTaskName,
} from '../utils';
import { useModalHandlers } from 'components/modal/hooks';
import { usePromptOnUnsavedChanges } from 'CustomHooks/generic';
import { loadTask, newDefaultTaskName } from '../constants';
import {
  updateImageTaskResultsList,
  useOnAddNewImageTask,
  useOnChangeImageTaskValue,
  useOnDeleteImageTask,
  useFetchImageStorage,
  useOnSetSelectedImageManagementTaskId,
  useUpdateIsSelectedTaskRunning,
  useUpdateIsPreview,
} from '../hooks';
import { useSetupImageStore } from 'redux/hooks/SetupTab';

const imageManagementTaskService = new ImageManagementTaskService();

const TaskManager = () => {
  const imageManagementTasks = useImageManagementTasks();
  const selectedImageManagementTaskId = useSelectedImageTaskId();
  const isDirty = useImageManagementTaskIsDirty();
  const onChangeImageTaskValue = useOnChangeImageTaskValue();
  const onAddImageTask = useOnAddNewImageTask();
  const deleteImageTaskById = useOnDeleteImageTask();
  const setSelectedImageTaskId = useOnSetSelectedImageManagementTaskId();
  const { showModal, hideModal } = useModalHandlers();
  const [selectedTaskName, setSelectedTaskName] = useState('');
  const onUpdateImageTaskResultsList = updateImageTaskResultsList();
  const setIsSelectedTaskRunning = useUpdateIsSelectedTaskRunning();
  const imageStoreList = useSetupImageStore();
  const onRefreshIamgeStorage = useFetchImageStorage();
  const [isStorageInfoBtnClicked, setIsStorageInfoBtnClicked] = useState(false);
  const setIsPreview = useUpdateIsPreview();
  const isImageManagementTaskListLoading = useIsImageManagementTaskListLoading();

  useEffect(() => {
    if (imageStoreList.length > 0 && isStorageInfoBtnClicked) {
      showStorageModal();
    }
  }, [imageStoreList]);

  const tasks = useMemo(() => {
    if (!imageManagementTasks) {
      return;
    }
    const taskOptions = imageManagementTasks.map((task) => {
      return {
        id: task.id,
        title: task.taskName,
        value: task.taskName,
        parentId: task.parentId,
        treeNodeId: task.treeNodeId,
        errorBehaviour: task.errorBehaviour,
        isBackground: task.isBackground,
        imageManagementActionList: task.imageManagementActionList,
      };
    });

    if (!taskOptions.some((task) => task.title === loadTask.title)) {
      taskOptions.push(loadTask);
    }

    taskOptions.sort((a, b) => a.id - b.id);
    return taskOptions;
  }, [imageManagementTasks]);

  const selectedTask = useMemo(() => {
    setIsPreview(false);
    const currentTask = imageManagementTasks?.find(
      (task) => task.id === selectedImageManagementTaskId,
    );
    const isRunning = currentTask?.imageManagementActionList?.some(
      (action) => action.isExecuting === true,
    );
    setIsSelectedTaskRunning(isRunning);
    setSelectedTaskName(currentTask?.taskName || '');
    return currentTask;
  }, [selectedImageManagementTaskId, imageManagementTasks]);

  const onAddTask = () => {
    addTask();
  };

  const addTask = async (newTaskName = undefined, isDuplicate = false) => {
    if (selectedTask && isDirty) {
      showUnsavedMessage(showModal, hideModal);
      return;
    }

    const newName = getUniqueTaskName(tasks, newTaskName || newDefaultTaskName);
    const parentId = tasks.find((task) => task.parentId > 0)?.parentId;

    let newTaskDTO: ImageManagementTaskDTO;

    if (isDuplicate) {
      const newDuplicatedTask = new ImageManagementTaskDTO({
        ...selectedTask,
        isDuplicate: true,
        taskName: newName,
        parentId: parentId,
      });
      newTaskDTO = deserializeImageTaskDTO(newDuplicatedTask);
    } else {
      newTaskDTO = { taskName: newName, parentId: parentId } as ImageManagementTaskDTO;
    }
    try {
      const newTask = await imageManagementTaskService.add(newTaskDTO);

      onAddImageTask(newTask);
      setSelectedImageTaskId(newTask.id);
      setIsSelectedTaskRunning(false);
      showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Task Added');
    } catch {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Failed to add task');
    }
  };

  const onDuplicateTask = () => {
    const duplicatedName = `Copy of ${selectedTask?.taskName}`;
    if (duplicatedName.length > TASK_NAME_MAXIMUM_LENGTH) {
      showLargerTaskNameMessage(showModal, hideModal, duplicatedName);
      return;
    }
    addTask(duplicatedName, true);
    setIsSelectedTaskRunning(false);
  };

  const onDeleteTask = async () => {
    try {
      await imageManagementTaskService.delete(selectedTask?.treeNodeId);
    } catch (err) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        err.ErrorMessage || 'Failed to delete task',
      );
      return;
    }
    setSelectedImageTaskId(0);
    setSelectedTaskName('');
    setIsSelectedTaskRunning(false);
    deleteImageTaskById(selectedImageManagementTaskId);
    showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Task Removed');
  };

  const onChangeTask = (e) => {
    if (selectedTask && isDirty) {
      showUnsavedMessage(showModal, hideModal);
      return;
    }
    setSelectedImageTaskId(tasks[e.target.selectedIndex]?.id);
    onUpdateImageTaskResultsList([]);
  };

  const onChangeTaskName = (e) => {
    setSelectedTaskName(e.target.value);
  };

  const onBlurTaskName = (e) => {
    if (
      selectedTaskName !== selectedTask?.taskName &&
      imageManagementTasks?.some((task) => task.taskName === e.target.value)
    ) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Task name already in use. ',
      );
      setSelectedTaskName(selectedTask?.taskName);
    } else {
      selectedTaskName !== selectedTask?.taskName &&
        onChangeImageTaskValue('taskName', e.target.value);
    }
  };

  const onClickStorageInfo = () => {
    onRefreshIamgeStorage();
    setIsStorageInfoBtnClicked(true);
    showStorageModal();
  };

  const showStorageModal = () => {
    showModal(getStorageInfoModalProps(imageStoreList, onRefreshIamgeStorage));
  };

  usePromptOnUnsavedChanges(isDirty);

  const allowEdits = useMemo(() => {
    return !!imageManagementTasks?.find((x) => x.id === selectedImageManagementTaskId)
      ?.allowEdits;
  }, [imageManagementTasks, selectedImageManagementTaskId]);

  return (
    <div
      id="taskmanager-wrapper"
      className="flex flex-row w-full h-[9.5%] items-center"
      style={styles.mainContainerStyle}
    >
      <div id="taskmanager-input" className="flex flex-row items-center w-[50%] h-[100%]">
        <div
          id="taskmanager-input-title"
          className="text-[14px] font-bold ml-[6px]"
          style={styles.inputTitle}
        >
          {'File Name'}
        </div>
        <input
          id="taskmanager-input-fielddetails"
          className="inputFieldDetails h-[56%] w-[82%] text-[14px] ml-[2%] text-[#313131] pr-[12px]"
          onChange={onChangeTaskName}
          onBlur={onBlurTaskName}
          value={selectedTaskName}
          disabled={false}
        />
      </div>
      <SelectInput
        options={tasks}
        style={styles.selectInput}
        value={'Load File'}
        onChange={onChangeTask}
      />
      <ButtonsListContainer
        containerStyleClass="commands-container ml-[1.5%]"
        buttonsList={taskButtons(
          onAddTask,
          onDuplicateTask,
          onDeleteTask,
          !allowEdits,
          isImageManagementTaskListLoading,
          !!selectedImageManagementTaskId,
        )}
      />
      <div id="taskmanager-storageinfo-button" className="w-[45%] flex justify-end mx-5">
        <RMCustomButton
          iconProps={{ name: 'StorageInfo', width: '200', height: '28' }}
          onClick={onClickStorageInfo}
          titleTip="StorageInfo"
          isDisabled={false}
        />
      </div>
    </div>
  );
};

const styles: IStyle = {
  mainContainerStyle: {
    borderBottom: '1px solid #E0E0E0',
  },
  inputContainer: {
    margin: '15px 30px',
  },
  inputTitle: {
    fontFamily: 'Roboto',
  },
  inputData: {
    padding: 0,
    width: '80%',
  },
  selectInput: {
    margin: '15px 0px',
    width: '9%',
    height: '55%',
    fontSize: '14px',
    color: '#333333',
    borderRadius: '2px',
  },
  buttonList: {
    margin: '15px',
  },
};

export default TaskManager;
